import React, { useEffect, useMemo, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Pagination from "@material-ui/lab/Pagination";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import GroupIcon from "@material-ui/icons/Group";
import CheckIcon from "@material-ui/icons/Check";
import BuildIcon from "@material-ui/icons/Build";
import BarChartIcon from "@material-ui/icons/BarChart";
import HowToRegIcon from "@material-ui/icons/HowToReg";
import BlockIcon from "@material-ui/icons/Block";
import CloseIcon from "@material-ui/icons/Close";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import ImportantDevicesIcon from "@material-ui/icons/ImportantDevices";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import PersonIcon from "@material-ui/icons/Person";
import manageUsersIcon from "../../assets/images/svg/user-gear-solid-full-navy.svg";
import { useDispatch } from "react-redux";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";
import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import ManageRoles from "../ManageRoles";
import AddUser from "../AddUser";

const ROWS_OPTIONS = [5, 10, 20];

const COLORS = {
  navyDark: "#1e40af",
  blue800: "#1e40af",
  blue700: "#1d4ed8",
  blue100: "#dbeafe",
  blue50: "#eff6ff",
  emerald700: "#047857",
  emerald50: "#ecfdf5",
  emerald200: "#a7f3d0",
  slate500: "#64748b",
  slate100: "#f1f5f9",
  slate200: "#e2e8f0",
  amber500: "#f59e0b",
  red500: "#ef4444",
  border: "#e2e8f0",
  muted: "#f1f5f9",
  mutedForeground: "#64748b",
};

const useStyles = makeStyles(() => ({

  root: {
    flexGrow: 1,
    padding: "24px",
    backgroundColor: "#F8FAFC",
    minHeight: "calc(100vh - 127px)",
  },

  main: {
    // maxWidth: 1400,
    margin: "0 auto",
  },

  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },

  pageHeaderLeft: {
    display: "flex",
    alignItems: "center",
  },

  pageIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
  },

  roleButton: {
    textTransform: "none",
    borderColor: COLORS.border,
    color: "#111827",
    marginRight: 12,
    borderRadius: 8,
    padding: "6px 16px",

    "& .MuiButton-label": {
      fontSize: "14px",
    },
  },

  addButton: {
    textTransform: "none",
    backgroundColor: COLORS.blue700,
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: COLORS.blue800,
    },
    "& .MuiButton-label": {
      fontSize: "13px",
    },
  },

  statCard: {
    padding: "20px 20px",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "all 0.15s",
  },

  statLabel: {
    color: COLORS.mutedForeground,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontSize: 12,
  },

  statValue: {
    marginTop: 4,
    fontWeight: 600,
  },

  statIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  tableCard: {
    borderRadius: 16,
    overflow: "hidden",
  },

  toolbar: {
    padding: "0px 0px 8px 24px",
    borderBottom: `1px solid ${COLORS.border}`,
  },

  searchField: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },
    "& .MuiOutlinedInput-root": {
      height: 50,
      width: 250,
      backgroundColor: COLORS.muted,
      borderRadius: 8
    },
  },

  searchIcon: {
    fontSize: 18,
    color: COLORS.mutedForeground,
  },

  table: {
    width: "100%"
  },

  tableHeadRow: {
    backgroundColor: "rgba(241,245,249,0.8)",
  },

  tableHeadCell: {
    fontSize: 12,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
  },

  editingRow: {
    backgroundColor: COLORS.navyDark,
    "&:hover": {
      backgroundColor: COLORS.navyDark,
    },
  },

  dimmedRow: {
    opacity: 0.4,
  },

  firstNameCell: {
    display: "flex",
    alignItems: "center",
  },

  avatarText: {
    marginLeft: 8,
  },

  editField: {
    width: "100%",

    "& .MuiOutlinedInput-root": {
      height: 40,
      backgroundColor: COLORS.muted,
      borderRadius: 8,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& .MuiInputBase-input": {
      fontSize: 12,
      color: "#000",
      padding: "10px 12px",
    },
  },

  editingText: {
    color: "rgba(255,255,255,0.9)",
  },

  makeChip: {
    backgroundColor: COLORS.blue50,
    color: COLORS.blue700,
    border: `1px solid ${COLORS.blue100}`,
    fontSize: "0.85rem",
  },

  activeStatusChip: {
    backgroundColor: COLORS.emerald50,
    color: COLORS.emerald700,
    border: `1px solid ${COLORS.emerald200}`,
    fontWeight: 500,
    fontSize: "0.85rem",
  },

  inactiveStatusChip: {
    backgroundColor: COLORS.slate100,
    color: COLORS.slate500,
    border: `1px solid ${COLORS.slate200}`,
    fontWeight: 500,
    fontSize: "0.85rem",
  },

  actionButton: {
    color: COLORS.mutedForeground,
    "&:hover": {
      color: COLORS.blue700,
      backgroundColor: COLORS.blue50,
    },
  },

  editingActionButton: {
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "#fff",
    marginRight: 4,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.3)",
    },
  },

  footer: {
    padding: "12px 24px",
    borderTop: `1px solid ${COLORS.border}`,
    backgroundColor: "rgba(241,245,249,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerLeft: {
    display: "flex",
    alignItems: "center",
  },

  footerText: {
    color: COLORS.mutedForeground,
    marginRight: 8,
  },

  rowsSelect: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      borderRadius: 8,
    },

    "& .MuiSelect-select": {
      padding: "5px 25px 5px 10px",
    },
  },

  footerRight: {
    display: "flex",
    alignItems: "center",
  },

  pageText: {
    color: COLORS.mutedForeground,
    marginRight: 12,
  },

  normalRow: {
    backgroundColor: "#fff",
    "& .MuiTypography-root": {
      color: "#000",
      fontSize: "0.85rem",
    },
    "&:hover": {
      backgroundColor: "rgba(241,245,249,0.5)",
    },
  },

  emptyCell: {
    padding: "48px 0",
    color: COLORS.mutedForeground,
  },

  tableIcon: {
    fill: "currentColor !important",
    width: "1.1em !important",
    height: "1.1em !important",
  },
}));

function getLoginColor(days) {
  if (Number(days) > 200) return COLORS.red500;
  if (Number(days) > 100) return COLORS.amber500;
  return COLORS.emerald700;
}

function getAvatarType(role) {
  const userRole = role || "";

  if (userRole.includes("Super Admin")) return "superAdmin";
  if (userRole.includes("Admin")) return "admin";
  if (userRole.includes("Analyst")) return "analyst";
  if (userRole.includes("Technician")) return "wrench";

  return "user";
}

function AvatarIcon({ type }) {
  let bg = COLORS.slate500;
  let Icon = PersonIcon;

  if (type === "wrench") {
    bg = COLORS.blue800;
    Icon = BuildIcon;
  } else if (type === "analyst") {
    bg = COLORS.blue800;
    Icon = BarChartIcon;
  } else if (type === "admin") {
    bg = COLORS.blue800;
    Icon = SupervisorAccountIcon;
  } else if (type === "superAdmin") {
    bg = COLORS.blue800;
    Icon = ImportantDevicesIcon;
  }

  return (
    <Avatar
      style={{
        width: 36,
        height: 36,
        backgroundColor: bg,
      }}
    >
      <Icon style={{ fontSize: 20, color: "#fff" }} />
    </Avatar>
  );
}

function StatusBadge({ status, classes }) {
  const isActive = status === true;

  return (
    <Chip
      size="small"
      icon={
        isActive ? (
          <CheckIcon style={{ fontSize: 12 }} />
        ) : undefined
      }
      label={isActive ? "Activated" : "Deactivated"}
      className={isActive ? classes.activeStatusChip : classes.inactiveStatusChip}
    />
  );
}

const ManageUsers = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [makeSelected] = useContext(Context);

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({
    firstName: "",
    lastName: "",
    status: true,
  });
  const [roleOpen, setRoleOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");


  const RefreshDataSet = async () => {
    if (!makeSelected || !makeSelected.uid) {
      setUsers([]);
      return;
    }

    dispatch(OAuthActions.getUsersAll(makeSelected.uid, 0, 999)).then(function (
      userDetails
    ) {
      const userList = userDetails?._embedded?.userLists || [];

      const newDataSet = userList.map((item) => ({
        uid: item.uid,
        firstName: item.firstName || "",
        lastName: item.lastName || "",
        email: item.userName || "",
        status: item.isActive,
        role: item.role?.description || "",
        make: item.make?.name || "",
        daysSinceLastLogin: item.daysSinceLastLogin,
        avatarType: getAvatarType(item.role?.description || ""),
      }));

      setUsers(newDataSet);
    });
  };

  useEffect(() => {
    RefreshDataSet();
  }, [makeSelected]);

  const updateDataSet = async (dataSet) => {
    const validFirstName = /^[a-zA-Z\s]+$/.test(dataSet.firstName);
    const validLastName = /^[a-zA-Z\s]+$/.test(dataSet.lastName);

    if (
      validFirstName &&
      dataSet.firstName.length !== 0 &&
      validLastName &&
      dataSet.lastName.length !== 0
    ) {
      await dispatch(
        OAuthActions.updateUsers(
          dataSet.uid,
          dataSet.firstName,
          dataSet.lastName,
          dataSet.status
        )
      ).then(function (userDetails) {
        if (userDetails["error"] === undefined) {
          RefreshDataSet();
          dispatch(setSnackbar(true, "success", "Update Successful"));
        } else {
          dispatch(
            setAlertDialog(
              true,
              null,
              "error",
              "Update User",
              "Update unsuccessful.\nDetails Of Failure: " + userDetails.message,
              "OK",
              ""
            )
          );
        }
      });
    } else {
      dispatch(
        setAlertDialog(
          true,
          null,
          "error",
          "Update User",
          "Please enter only Alphabets in First Name and Last Name",
          "OK",
          ""
        )
      );
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return users.filter((u) => {
      const matchesSearch =
        (u.firstName || "").toLowerCase().includes(q) ||
        (u.lastName || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q) ||
        (u.make || "").toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Activated" && u.status === true) ||
        (statusFilter === "Deactivated" && u.status === false);

      return matchesSearch && matchesStatus;
    });
  }, [search, users, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const activeCount = users.filter((u) => u.status === true).length;
  const inactiveCount = users.filter((u) => u.status === false).length;

  const statCards = [
    {
      label: "Total Users",
      filter: "All",
      value: users.length,
      icon: <GroupIcon style={{ fontSize: 22 }} />,
      color: COLORS.blue700,
      bg: COLORS.blue50,
    },
    {
      label: "Active",
      filter: "Activated",
      value: activeCount,
      icon: <HowToRegIcon style={{ fontSize: 22 }} />,
      color: COLORS.emerald700,
      bg: COLORS.emerald50,
    },
    {
      label: "Inactive",
      filter: "Deactivated",
      value: inactiveCount,
      icon: <BlockIcon style={{ fontSize: 22 }} />,
      color: COLORS.slate500,
      bg: COLORS.slate100,
    },
  ];

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleAddUser = () => {
    setAddUserOpen(true);
  };

  const startEdit = (user) => {
    setEditingId(user.uid);
    setDraft({
      firstName: user.firstName,
      lastName: user.lastName,
      status: user.status,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({
      firstName: "",
      lastName: "",
      status: true,
    });
  };

  const saveEdit = (user) => {
    const updatedData = {
      uid: user.uid,
      firstName: draft.firstName,
      lastName: draft.lastName,
      status: draft.status,
    };

    if (
      updatedData.firstName === user.firstName &&
      updatedData.lastName === user.lastName &&
      updatedData.status === user.status
    ) {
      cancelEdit();
      return;
    }

    updateDataSet(updatedData);
    setEditingId(null);
  };

  return (
    <Box className={classes.root}>
      <Box component="main" className={classes.main}>
        <Box className={classes.pageHeader}>
          <Box className={classes.pageHeaderLeft}>
            <Box className={classes.pageIconBox}>
              <img src={manageUsersIcon} alt="Manage Users" style={{ width: 22, height: 22, filter: "brightness(0) invert(1)" }} />
            </Box>

            <Typography variant="h5">
              <strong style={{ fontSize: "1.4rem" }}>Manage Users</strong>
            </Typography>
          </Box>

          <Box className={classes.headerActions}>
            <Button
              variant="outlined"
              startIcon={<VerifiedUserIcon style={{ color: COLORS.blue700 }} />}
              onClick={() => setRoleOpen(true)}
              className={classes.roleButton}
            >
              Roles
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              className={classes.addButton}
              style={{ fontSize: 20 }}
            >
              Add User
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          {statCards.map((s) => {
            const isActive = statusFilter === s.filter;

            return (
              <Grid item xs={12} sm={4} key={s.label}>
                <Paper
                  variant="outlined"
                  onClick={() => {
                    setStatusFilter(s.filter);
                    setPage(1);
                  }}
                  className={classes.statCard}
                  style={{
                    borderColor: isActive ? s.color : COLORS.border,
                    boxShadow: isActive ? "0px 2px 8px rgba(0,0,0,0.15)" : "none",
                  }}
                >
                  <Box>
                    <Typography className={classes.statLabel}>
                      {s.label}
                    </Typography>

                    <Typography
                      variant="h3"
                      className={classes.statValue}
                      style={{ color: s.color }}
                    >
                      {s.value}
                    </Typography>
                  </Box>

                  <Box
                    className={classes.statIconBox}
                    style={{
                      backgroundColor: s.bg,
                      color: s.color,
                    }}
                  >
                    {s.icon}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Paper variant="outlined" className={classes.tableCard}>
          <Box className={classes.toolbar}>
            <TextField
              size="small"
              variant="outlined"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search users..."
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  {[
                    { label: "First Name", width: "13%" },
                    { label: "Last Name", width: "10%" },
                    { label: "Email ID", width: "18%" },
                    { label: "Role", width: "17%" },
                    { label: "Make", width: "9%" },
                    { label: "Last Login (days)", width: "12%" },
                    { label: "Status", width: "12%" },
                    { label: "Actions", width: "9%" },
                  ].map(({ label, width }) => (
                    <TableCell
                      key={label}
                      className={classes.tableHeadCell}
                      style={{ width }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map((user) => {
                  const isEditing = editingId === user.uid;
                  const isDimmed = editingId !== null && !isEditing;

                  return (
                    <TableRow
                      key={user.uid}
                      className={`${isEditing ? classes.editingRow : classes.normalRow} ${isDimmed ? classes.dimmedRow : ""
                        }`}
                    >
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            variant="outlined"
                            value={draft.firstName}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                firstName: e.target.value,
                              }))
                            }
                            className={classes.editField}
                          />
                        ) : (
                          <Box className={classes.firstNameCell}>
                            <AvatarIcon type={user.avatarType} />

                            <Typography
                              variant="caption"
                              noWrap
                              className={classes.avatarText}
                            >
                              {user.firstName}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            variant="outlined"
                            value={draft.lastName}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                lastName: e.target.value,
                              }))
                            }
                            className={classes.editField}
                          />
                        ) : (
                          <Typography variant="caption" noWrap display="block">
                            {user.lastName}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          noWrap
                          display="block"
                          className={isEditing ? classes.editingText : ""}
                        >
                          {user.email}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          noWrap
                          display="block"
                          className={isEditing ? classes.editingText : ""}
                        >
                          {user.role}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <Typography
                            variant="caption"
                            className={classes.editingText}
                          >
                            {user.make}
                          </Typography>
                        ) : (
                          <Chip
                            label={user.make}
                            size="small"
                            className={classes.makeChip}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          style={{
                            fontWeight: 500,
                            color: isEditing
                              ? "rgba(255,255,255,0.9)"
                              : getLoginColor(user.daysSinceLastLogin),
                          }}
                        >
                          {user.daysSinceLastLogin}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <Select
                            style={{ borderRadius: 8 }}
                            size="small"
                            variant="outlined"
                            value={draft.status}
                            MenuProps={{
                              PaperProps: {
                                style: { borderRadius: 8 },
                              },
                            }}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                status: e.target.value,
                              }))
                            }
                            className={classes.editField}
                          >
                            <MenuItem value={true}>Activated</MenuItem>
                            <MenuItem value={false}>Deactivated</MenuItem>
                          </Select>
                        ) : (
                          <StatusBadge status={user.status} classes={classes} />
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => saveEdit(user)}
                              className={classes.editingActionButton}
                            >
                              <CheckIcon className={classes.tableIcon} />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={cancelEdit}
                              className={classes.editingActionButton}
                            >
                              <CloseIcon className={classes.tableIcon} />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => startEdit(user)}
                            className={classes.actionButton}
                          >
                            <EditIcon className={classes.tableIcon} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {paginated.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      className={classes.emptyCell}
                    >
                      No users match your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.footer}>
            <Box className={classes.footerLeft}>
              <Typography variant="body2" className={classes.footerText}>
                Rows per page:
              </Typography>

              <Select
                style={{ borderRadius: 8 }}
                size="small"
                variant="outlined"
                value={rowsPerPage}
                onChange={handleRowsPerPage}
                className={classes.rowsSelect}
              >
                {ROWS_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box className={classes.footerRight}>
              <Typography variant="body2" className={classes.pageText}>
                Page {page} of {totalPages}
              </Typography>

              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                shape="circular"
                size="small"
              />
            </Box>
          </Box>
        </Paper>
      </Box>
      <Dialog
        open={roleOpen}
        onClose={() => setRoleOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          style: {
            width: "50%",
            borderRadius: 12,
          },
        }}
      >
        <DialogContent
          style={{
            padding: 0,
            overflow: "hidden",
          }}
        >
          <ManageRoles
            onClose={() => setRoleOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          style: {
            width: "40%",
            borderRadius: 12,
          },
        }}
      >
        <DialogContent
          style={{
            padding: 0,
            overflow: "hidden",
          }}
        >
          <AddUser
            {...props}
            onClose={() => {
              setAddUserOpen(false);
              RefreshDataSet();
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ManageUsers;